import {
  Button,
  Card,
  Collapse,
  ControlGroup,
  Elevation,
  Spinner,
  Tag,
} from "@blueprintjs/core";
import React from "react";
import {
  apiCallPost,
  areTwoDatesSame,
  niceDateTime,
  niceTime,
} from "../fns/util";
import { inject, observer } from "mobx-react";
import cogoToast from "cogo-toast";
import _ from "lodash";
import { withRouter } from "react-router";
import SingleIDSelectWrapper from "../utils/SingleIDSelectWrapper";
import SingleSelectWrapper from "../utils/SingleSelectWrapper";
import { DatePicker, TimePicker } from "@blueprintjs/datetime";
import { shouldIncludeBookingInSlotMetrics } from "../../v3/utils/surgerySlotUtils";

class SxCalendar extends React.Component {
  state = {
    collapsed: true,
    /** CALENDAR STUFF */
    isLoadingCalendar: false,
    startDate: new Date(),
    endDate: new Date(),
    docCalendarLoaded: {},
    selectedDay: null,
    loadedDataForDay: {},
    chosenTime: null,
    /** LOAD VISIT DETAILS */
  };
  stripCancelledBookings = (list) => {
    if (!Array.isArray(list)) {
      return { filtered: list, changed: false };
    }
    const filtered = list.filter((entry) =>
      shouldIncludeBookingInSlotMetrics(entry)
    );
    return { filtered, changed: filtered.length !== list.length };
  };

  sanitizeCalendarDay = (dayData) => {
    if (!dayData || typeof dayData !== "object") {
      return dayData;
    }
    const candidateKeys = [
      "bookedSx",
      "bookedSurgeries",
      "bookedEncounters",
      "bookings",
    ];
    const sanitizedDay = { ...dayData };
    for (const key of candidateKeys) {
      if (Array.isArray(dayData[key])) {
        const { filtered, changed } = this.stripCancelledBookings(dayData[key]);
        sanitizedDay[key] = filtered;
        if (changed) {
          sanitizedDay.booked = filtered.length;
        }
        break;
      }
    }
    return sanitizedDay;
  };

  sanitizeCalendarResponse = (calendarResponse) => {
    if (!calendarResponse || typeof calendarResponse !== "object") {
      return calendarResponse || {};
    }
    return Object.keys(calendarResponse).reduce((acc, dateKey) => {
      acc[dateKey] = this.sanitizeCalendarDay(calendarResponse[dateKey]);
      return acc;
    }, {});
  };

  sanitizeSlotResponse = (slotsResponse) => {
    if (!slotsResponse || typeof slotsResponse !== "object") {
      return slotsResponse || {};
    }
    return Object.keys(slotsResponse).reduce((acc, slotKey) => {
      const slotData = slotsResponse[slotKey];
      if (!slotData || typeof slotData !== "object") {
        acc[slotKey] = slotData;
        return acc;
      }
      const sanitizedSlot = { ...slotData };
      if (Array.isArray(slotData.bookedSx)) {
        const { filtered, changed } = this.stripCancelledBookings(
          slotData.bookedSx
        );
        sanitizedSlot.bookedSx = filtered;
        if (changed || typeof slotData.booked === "number") {
          sanitizedSlot.booked = filtered.length;
        }
      }
      acc[slotKey] = sanitizedSlot;
      return acc;
    }, {});
  };

  deriveBookedTotalFromSlots = (slots = {}) => {
    return Object.keys(slots).reduce((sum, slotKey) => {
      const slot = slots[slotKey];
      if (!slot || typeof slot !== "object") {
        return sum;
      }
      if (Array.isArray(slot.bookedSx)) {
        return sum + slot.bookedSx.length;
      }
      if (typeof slot.booked === "number") {
        return sum + slot.booked;
      }
      return sum;
    }, 0);
  };

  applySanitizedSlotSummary = (
    docCalendarLoaded,
    selectedDay,
    sanitizedSlots
  ) => {
    if (
      !docCalendarLoaded ||
      !selectedDay ||
      _.isEmpty(sanitizedSlots) ||
      _.isEmpty(docCalendarLoaded)
    ) {
      return docCalendarLoaded;
    }
    const keys = _.keys(docCalendarLoaded);
    const idx = _.findIndex(keys, (each) => areTwoDatesSame(each, selectedDay));
    if (idx === -1) {
      return docCalendarLoaded;
    }
    const dateKey = keys[idx];
    const totalBooked = this.deriveBookedTotalFromSlots(sanitizedSlots);
    if (!Number.isFinite(totalBooked)) {
      return docCalendarLoaded;
    }
    return {
      ...docCalendarLoaded,
      [dateKey]: {
        ...docCalendarLoaded[dateKey],
        booked: totalBooked,
      },
    };
  };

  getCalendarData = async () => {
    try {
      cogoToast.loading("Loading calendar for the month...");
      this.setState({
        isLoadingCalendar: true,
        docCalendarLoaded: {},
        loadedDataForDay: {},
        selectedDay: null,
      });
      let res = await apiCallPost(
        `/booking-sx/${this.props.authStore.prefix}/getSxCalendar`,
        {
          site: this.props.data.site,
          doctorId: this.props.data.surgeonId,
          startDate: this.state.startDate,
          endDate: this.state.endDate,
        }
      );
      const sanitized = this.sanitizeCalendarResponse(res);
      this.setState({ isLoadingCalendar: false, docCalendarLoaded: sanitized });
    } catch (err) {
      cogoToast.error("Failed to load calendar.");
      this.setState({ isLoadingCalendar: false });
    }
  };
  getDataForDay = async () => {
    let selectedDay = this.state.selectedDay;
    if (!selectedDay) {
      return null;
    }
    let dcl = this.state.docCalendarLoaded;
    let dclKeys = _.keys(dcl);
    let idx = _.findIndex(dclKeys, (each) =>
      areTwoDatesSame(each, selectedDay)
    );
    let dataForTheDay = dcl[dclKeys[idx]];
    let s = dataForTheDay["status"];
    if (s !== "OK") {
      cogoToast.error("You can't book for this day.");
      return;
    }
    let b = dataForTheDay["booked"];
    let m = dataForTheDay["maximum"];
    if (m === 0) {
      cogoToast.error("You can't book for this day.");
      return;
    }
    if (b >= m) {
      cogoToast.error("You can't book for this day.");
      return;
    }
    try {
      cogoToast.loading("Loading data for the day...");
      this.setState({ isLoadingDay: true });
      let res = await apiCallPost(
        `/booking-sx/${this.props.authStore.prefix}/getSxCalendarSlots`,
        {
          site: this.props.data.site,
          doctorId: this.props.data.surgeonId,
          date: new Date(this.state.selectedDay),
        }
      );
      const sanitizedSlots = this.sanitizeSlotResponse(res);
      this.setState((prev) => ({
        isLoadingDay: false,
        loadedDataForDay: sanitizedSlots,
        docCalendarLoaded: this.applySanitizedSlotSummary(
          prev.docCalendarLoaded,
          prev.selectedDay,
          sanitizedSlots
        ),
      }));
    } catch (err) {
      cogoToast.error("Failed to load calendar.");
      this.setState({ isLoadingDay: false });
    }
  };
  renderSurgeonSearch = () => {
    if (this.props.data.surgeonId) {
      return (
        <ControlGroup>
          <Tag intent="primary" large minimal>
            Surgeon
          </Tag>
          <Tag intent="primary" large>
            {this.props.data.surgeonName}
          </Tag>
          {["DRAFT", "BOOKED"].includes(this.props.data.lifecycle) && (
            <Button
              icon="edit"
              outlined
              text="Edit"
              onClick={() => {
                this.props.onUpdate({
                  surgeonId: null,
                  surgeonName: "",
                  site: "",
                });
              }}
            />
          )}
        </ControlGroup>
      );
    }
    return (
      <ControlGroup>
        {" "}
        <Tag large minimal>
          Surgeon
        </Tag>{" "}
        <SingleIDSelectWrapper
          showSelectProp={true}
          width="200px"
          items={this.props.docs.map((each) => {
            return {
              ...each,
              searchName: `${each.shortcode || ""} | ${each.name}`,
            };
          })}
          selectedItem={this.state.selectedSurgeon}
          labelKey="searchName"
          onChange={(item) => {
            this.props.onUpdate({
              surgeonId: item._id,
              surgeonName: item.name,
            });
          }}
        />
      </ControlGroup>
    );
  };
  renderAnaesthetistSearch = () => {
    if (this.props.data.anaesthetistId) {
      return (
        <ControlGroup>
          <Tag intent="warning" large minimal>
            Anaesthetist
          </Tag>
          <Tag intent="warning" large>
            {this.props.data.anaesthetistName}
          </Tag>
          {["DRAFT", "BOOKED", "BILLED"].includes(
            this.props.data.lifecycle
          ) && (
            <Button
              icon="edit"
              outlined
              text="Edit"
              onClick={() => {
                this.props.onUpdate({
                  anaesthetistId: null,
                  anaesthetistName: "",
                });
              }}
            />
          )}
        </ControlGroup>
      );
    }
    return (
      <ControlGroup>
        {" "}
        <Tag large minimal>
          Anaesthetist
        </Tag>{" "}
        <SingleIDSelectWrapper
          showSelectProp={true}
          width="200px"
          items={this.props.docs.map((each) => {
            return {
              ...each,
              searchName: `${each.shortcode || ""} | ${each.name}`,
            };
          })}
          selectedItem={this.state.selectedSurgeon}
          labelKey="searchName"
          onChange={(item) => {
            this.props.onUpdate({
              anaesthetistId: item._id,
              anaesthetistName: item.name,
            });
          }}
        />
      </ControlGroup>
    );
  };
  renderSecondarySurgeonSearch = () => {
    let terminals = ["DISCHARGED", "LAMA", "DEATH", "CANCELLED"];
    if (terminals.includes(this.props.data.lifecycle)) {
      return null;
    }
    return (
      <ControlGroup>
        {" "}
        <SingleIDSelectWrapper
          showSelectProp={true}
          width="200px"
          items={this.props.docs.map((each) => {
            return {
              ...each,
              searchName: `${each.shortcode || ""} | ${each.name}`,
            };
          })}
          selectedItem={this.state.selectedSurgeon}
          labelKey="searchName"
          onChange={(item) => {
            this.props.onUpdate({
              secondarySurgeons: [
                ...this.props.data.secondarySurgeons,
                { surgeonId: item._id, surgeonName: item.name },
              ],
            });
          }}
        />
      </ControlGroup>
    );
  };
  renderSecondarySurgeons = () => {
    if (!this.props.data.secondarySurgeons) {
      return null;
    }
    if (!this.props.data.secondarySurgeons.length) {
      return null;
    }
    let ss = this.props.data.secondarySurgeons;
    return ss.map((each, idx) => {
      return (
        <ControlGroup>
          <Tag
            intent="warning"
            minimal
            large
            onRemove={() => {
              let ss = this.props.data.secondarySurgeons;
              let index = _.findIndex(
                ss,
                (x) => x.surgeonId === each.surgeonId
              );
              ss.splice(index, 1);
              this.props.onUpdate({
                secondarySurgeons: ss,
              });
            }}
          >
            Secondary Surgeon {idx + 1}
          </Tag>
          <Tag intent="warning" large>
            {each.surgeonName}
          </Tag>
        </ControlGroup>
      );
    });
  };
  renderSiteSearch = () => {
    if (!this.props.data.surgeonId) {
      return null;
    }
    if (this.props.data.site) {
      return (
        <ControlGroup style={{ marginRight: "5px" }}>
          <Tag intent="primary" large minimal>
            Site
          </Tag>
          <Tag intent="primary" large>
            {this.props.data.site}
          </Tag>
          {["DRAFT", "BOOKED"].includes(this.props.data.lifecycle) && (
            <Button
              icon="edit"
              outlined
              text="Edit"
              onClick={() => {
                this.props.onUpdate({ site: "" });
              }}
            />
          )}
        </ControlGroup>
      );
    }
    return (
      <ControlGroup>
        <Tag large minimal>
          Site
        </Tag>
        <SingleSelectWrapper
          items={this.props.sites}
          showSelectProp={true}
          selectedItem={this.props.data.site}
          onChange={(site) => {
            this.props.onUpdate({ site: site });
          }}
        />
      </ControlGroup>
    );
  };
  renderGetCalendarData = () => {
    if (!this.props.data.surgeonId) {
      return null;
    }
    if (!this.props.data.site) {
      return null;
    }
    if (this.props.data.time) {
      return (
        <ControlGroup>
          <Tag intent="success" large minimal>
            Chosen:
          </Tag>
          <Tag intent="success" large>
            {this.props.data.time ? niceDateTime(this.props.data.time) : ""}
          </Tag>
          {["DRAFT", "BOOKED"].includes(this.props.data.lifecycle) && (
            <Button
              icon="edit"
              outlined
              text="Edit"
              onClick={() => this.props.onUpdate({ time: null })}
            />
          )}
        </ControlGroup>
      );
    }
    return (
      <Button
        onClick={() => {
          let today = new Date();
          let firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
          let lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
          this.setState(
            { startDate: firstDay, endDate: lastDay, selectedDay: null },
            () => {
              this.getCalendarData();
            }
          );
        }}
        outlined
        text="Get Availability Calendar"
        icon="timeline-events"
      />
    );
  };
  renderDay(day) {
    const selectedDay = day.getDate();
    let dcl = this.state.docCalendarLoaded;
    let dclKeys = _.keys(dcl);
    let idx = _.findIndex(dclKeys, (each) => areTwoDatesSame(each, day));
    const cellStyle = {
      position: "relative",
      margin: "5px",
    };
    const centerStyle = { textAlign: "center", marginBottom: "5px" };
    if (idx <= -1) {
      return (
        <div>
          <div style={cellStyle} className="bp3-dark">
            <ControlGroup vertical>
              <Tag minimal intent="none">
                NA
              </Tag>
              <div style={centerStyle}>{selectedDay}</div>
            </ControlGroup>
          </div>
        </div>
      );
    }
    let dataForTheDay = dcl[dclKeys[idx]];
    let s = dataForTheDay["status"];
    let sFlag = true;
    let b = dataForTheDay["booked"];
    let m = dataForTheDay["maximum"];
    let i = "none";
    if (s === "OK") {
      sFlag = false;
      if (m === 0) {
        sFlag = true;
        i = "none";
      } else {
        if (b / m > 0.8) {
          i = "danger";
        } else if (b / m > 0.6) {
          i = "warning";
        } else {
          i = "success";
        }
      }
    }
    return (
      <div>
        <div style={cellStyle} className="bp3-dark">
          <ControlGroup vertical>
            <div style={centerStyle}>{selectedDay}</div>
            <Tag intent={i} minimal={i === "none" ? true : false}>
              {sFlag ? (
                <>{m === 0 ? "NA" : <>{_.startCase(_.lowerCase(s))}</>}</>
              ) : (
                <>
                  {b}/{m}
                </>
              )}
            </Tag>
          </ControlGroup>
        </div>
      </div>
    );
  }
  renderCalendar = () => {
    if (this.props.data.time) {
      return null;
    }
    let today = new Date();
    let maxDate = new Date(today.setMonth(today.getMonth() + 15));
    if (!this.props.data.site) {
      return null;
    }
    if (!this.props.data.surgeonId) {
      return null;
    }
    if (_.isEmpty(this.state.docCalendarLoaded)) {
      return null;
    }
    if (this.state.isLoadingCalendar) {
      return <Spinner />;
    }
    return (
      <div
        style={{
          border: "1px solid rgba(255, 255, 255, 0.4)",
          display: "flex",
          justifyContent: "center",
          marginTop: "1rem",
        }}
      >
        <DatePicker
          dayPickerProps={{
            firstDayOfWeek: 1,
            onMonthChange: (date) => {
              let firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
              let lastDay = new Date(
                date.getFullYear(),
                date.getMonth() + 1,
                0
              );
              this.setState({ startDate: firstDay, endDate: lastDay }, () => {
                this.getCalendarData();
              });
            },
            renderDay: (day) => this.renderDay(day),
            showOutsideDays: false,
          }}
          onChange={(date) => {
            let selectedDay = date;
            if (!selectedDay) {
              return null;
            }
            let dcl = this.state.docCalendarLoaded;
            let dclKeys = _.keys(dcl);
            let idx = _.findIndex(dclKeys, (each) =>
              areTwoDatesSame(each, selectedDay)
            );
            let dataForTheDay = dcl[dclKeys[idx]];
            let s = dataForTheDay["status"];
            if (s !== "OK") {
              cogoToast.error("You can't book for this day.");
              return;
            }
            let b = dataForTheDay["booked"];
            let m = dataForTheDay["maximum"];
            if (m === 0) {
              cogoToast.error("You can't book for this day.");
              return;
            }
            if (b >= m) {
              cogoToast.error("You can't book for this day.");
              return;
            }
            this.setState({ selectedDay: date }, () => this.getDataForDay());
          }}
          maxDate={new Date(maxDate)}
          minDate={new Date()}
          initialMonth={new Date(this.state.startDate)}
          locale="en-IN"
        />
      </div>
    );
  };
  renderDataForDay = () => {
    if (this.props.data.time) {
      return null;
    }
    if (!this.state.selectedDay) {
      return null;
    }
    if (this.state.isLoadingDay) {
      return <Spinner size={20} />;
    }
    if (_.isEmpty(this.state.loadedDataForDay)) {
      return null;
    }
    let keys = _.keys(this.state.loadedDataForDay);
    return keys.map((each) => {
      return (
        <div style={{ marginTop: "10px" }}>{this.renderEachSlot(each)}</div>
      );
    });
  };
  renderEachSlot = (key) => {
    let data = this.state.loadedDataForDay[key];
    let b = data.booked;
    let m = data.maximum;
    let i = "none";
    if (m === 0) {
      i = "none";
    } else if (b / m > 0.8) {
      i = "danger";
    } else if (b / m > 0.6) {
      i = "warning";
    } else {
      i = "success";
    }
    let tmStart = new Date(data.tmStart);
    let tmEnd = new Date(data.tmEnd);
    tmEnd.setMinutes(tmEnd.getMinutes() - 10);
    return (
      <ControlGroup style={{ marginLeft: "5px" }}>
        <ControlGroup
          style={{
            marginLeft: "5px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ControlGroup>
            <Tag minimal large>
              Slot Name: {data.slotName}
            </Tag>
          </ControlGroup>
          <ControlGroup>
            <Tag
              large
              intent="primary"
              minimal={this.state.chosenSlotKey === key ? false : true}
              interactive
              onClick={() =>
                this.setState({
                  chosenSlotKey: key,
                  chosenTime: new Date(data.tmStart),
                })
              }
            >
              Slot: {key}
            </Tag>
            <Tag intent={i} large minimal>
              {data.booked}/{data.maximum}
            </Tag>
          </ControlGroup>
          {this.state.chosenSlotKey === key ? (
            <ControlGroup>
              <Tag minimal large>
                Choose Time
              </Tag>
              <TimePicker
                value={this.state.chosenTime}
                onChange={(time) => this.setState({ chosenTime: time })}
                maxTime={new Date(tmEnd)}
                minTime={new Date(tmStart)}
              />
              {this.state.chosenTime ? (
                <Button
                  icon="endorsed"
                  intent="success"
                  text={`Choose ${niceTime(this.state.chosenTime)}`}
                  onClick={() => {
                    let time = new Date(this.state.selectedDay);
                    let hh = new Date(this.state.chosenTime).getHours();
                    let mm = new Date(this.state.chosenTime).getMinutes();
                    let ts = new Date(time.setHours(hh, mm, 0, 0));
                    this.props.onUpdate({ time: ts });
                  }}
                />
              ) : null}
            </ControlGroup>
          ) : null}
        </ControlGroup>
        {data.booked ? (
          <ControlGroup vertical>
            <ControlGroup>
              <Tag minimal large>
                Booked Sx: {data.booked}
              </Tag>
            </ControlGroup>
            <ControlGroup vertical>{this.renderBookedSx(data)}</ControlGroup>
          </ControlGroup>
        ) : null}
      </ControlGroup>
    );
  };
  renderBookedSx = (data) => {
    let list = Array.isArray(data.bookedSx) ? data.bookedSx : [];
    if (!list.length) {
      return null;
    }
    return list.map((each) => {
      return (
        <ControlGroup>
          <Tag large minimal>
            {this.niceSxString(each)}
          </Tag>
        </ControlGroup>
      );
    });
  };
  niceSxString = (sxObj) => {
    let ll = sxObj.lineItems;
    let ll2 = _.filter(ll, (each) => each.lineItemType === "SX").filter(
      Boolean
    );

    let sxStr = ll2.map((each) => each.entityName).join(" + ");
    let time = niceTime(sxObj.time);
    return `${time}: ${sxStr}`;
  };
  renderBasedOnLifecycle = () => {
    return (
      <>
        <Collapse isOpen={this.state.collapsed}>
          <Card elevation={Elevation.FOUR}>
            <ControlGroup style={{ marginBottom: "10px" }}>
              <Tag large intent="primary">
                Surgeon Details
              </Tag>
              <Tag large minimal>
                Surgeon: {this.props.data.surgeonName}
              </Tag>
              <Tag large minimal>
                Time:{" "}
                {this.props.data.time ? niceDateTime(this.props.data.time) : ""}
              </Tag>
              <Tag large minimal>
                Location: {this.props.data.site}
              </Tag>
              <Button
                icon="double-chevron-down"
                outlined
                onClick={() => this.setState({ collapsed: false })}
              />
            </ControlGroup>
          </Card>{" "}
        </Collapse>
        <Collapse isOpen={!this.state.collapsed}>
          <Card elevation={Elevation.FOUR} className="dark__bg">
            <ControlGroup style={{ marginBottom: "10px" }}>
              <Tag large fill intent="primary">
                Surgeon Details
              </Tag>
              <Button
                icon="double-chevron-down"
                outlined
                onClick={() => this.setState({ collapsed: true })}
              />
            </ControlGroup>
            <div
              style={
                this.props.canEdit
                  ? {
                      display: "flex",
                      flexWrap: "wrap",
                    }
                  : {
                      "pointer-events": "none",
                      opacity: 0.5,
                      display: "flex",
                      flexWrap: "wrap",
                    }
              }
            >
              <div>
                <ControlGroup style={{ marginBottom: "5px" }}>
                  {this.renderSurgeonSearch()}
                </ControlGroup>
                <ControlGroup style={{ marginBottom: "5px" }}>
                  {this.renderAnaesthetistSearch()}
                </ControlGroup>
                <ControlGroup style={{ marginBottom: "5px" }}>
                  <Tag large minimal intent="warning">
                    Secondary Surgeons
                  </Tag>{" "}
                  {this.renderSecondarySurgeonSearch()}
                </ControlGroup>
                {this.renderSecondarySurgeons()}
              </div>

              <div style={{ marginLeft: "10rem", display: "flex" }}>
                <div>
                  <ControlGroup>
                    <br />
                    {this.renderSiteSearch()}
                    {this.renderGetCalendarData()}
                  </ControlGroup>
                  {this.renderCalendar()}
                </div>
                <div>
                  <ControlGroup vertical>
                    {this.renderDataForDay()}
                  </ControlGroup>
                </div>
              </div>
            </div>
          </Card>
        </Collapse>
      </>
    );
  };
  render() {
    return <div>{this.renderBasedOnLifecycle()}</div>;
  }
}

export default inject("authStore")(observer(withRouter(SxCalendar)));
